import * as React from "react";
import { Box, Button, Checkbox, FormControlLabel } from "@mui/material";
import { useSelector } from "react-redux";
import { RootReducerType, RootState } from "@store/store";
import { todayFormatted } from "../../../../src/utils/customUtils";
import { useLocation } from "react-router-dom";
import { ConsentData } from "../types";
type ConsentDetailProps = {
  document_id: number;
  property_type?: string;
};

const ConsentDetail: React.FC<ConsentDetailProps> = ({ document_id }) => {
  const location = useLocation();

  const isLegacyFlow = React.useMemo(() => {
    return (
      location.pathname.includes("legacy") || location.search.includes("access")
    );
  }, [location]);

  const legacy = useSelector((state: RootReducerType) => state.accessLegacy);

  const { profile, loading, error } = useSelector(
    (state: RootState) => state.profile,
  );

  const amenities = useSelector((state: RootReducerType) => state.amenities);

  const { applicationDetails } = useSelector(
    (state: RootReducerType) => state.applicationDetails,
  );

  const fieldsToCheck = [
    "start_chainage_km",
    "start_chainage_m",
    "end_chainage_km",
    "end_chainage_m",
    "district",
    "khasra_number",
    "national_highway_number",
    "pincode",
    "state",
    "village",
    "address_1",
    "address_2",
    "highway_side",
    "start_chainage",
    "end_chainage",
  ];

  const isAmenitiesEmpty =
    location.pathname.includes("application-details") ||
    fieldsToCheck.every((key) => {
      const value = amenities?.[key as keyof typeof amenities];
      return value === "" || value === null || value === undefined;
    });

  const resolvedData: ConsentData = React.useMemo(() => {
    if (isLegacyFlow) {
      return {
        district: legacy?.district,
        khasra_number: legacy?.khasra_number,
        national_highway_number: legacy?.national_highway_number,
        pincode: legacy?.pincode ? String(legacy.pincode) : undefined,
        state: legacy?.state,
        village: legacy?.village,
        address_1: legacy?.address_1,
        address_2: legacy?.address_2,
        highway_side: legacy?.highway_side,
        start_chainage_km: legacy?.start_chainage_km,
        start_chainage_m: legacy?.start_chainage_m,
        end_chainage_km: legacy?.end_chainage_km,
        end_chainage_m: legacy?.end_chainage_m,
        start_chainage: legacy?.start_chainage,
        end_chainage: legacy?.end_chainage,
      };
    }

    if (amenities) {
      return {
        district: amenities?.district,
        khasra_number: amenities?.khasra_number,
        national_highway_number: amenities?.national_highway_number,
        pincode: amenities?.pincode ? String(amenities.pincode) : undefined,
        state: amenities?.state,
        village: amenities?.village,
        address_1: amenities?.address_1,
        address_2: amenities?.address_2,
        highway_side: amenities?.highway_side,
        start_chainage_km: amenities?.start_chainage_km,
        start_chainage_m: amenities?.start_chainage_m,
        end_chainage_km: amenities?.end_chainage_km,
        end_chainage_m: amenities?.end_chainage_m,
        start_chainage: amenities?.start_chainage,
        end_chainage: amenities?.end_chainage,
      };
    }

    return {
      district: applicationDetails?.district,
      khasra_number: applicationDetails?.khasra_number,
      national_highway_number: applicationDetails?.national_highway_number,
      pincode: applicationDetails?.pincode
        ? String(applicationDetails.pincode)
        : undefined,
      state: applicationDetails?.state,
      village: applicationDetails?.village,
      address_1: applicationDetails?.address_1,
      address_2: applicationDetails?.address_2,
      highway_side: applicationDetails?.highway_side,
      start_chainage: applicationDetails?.start_chainage,
      end_chainage: applicationDetails?.end_chainage,
      chainage: applicationDetails?.chainage,
    };
  }, [isLegacyFlow, legacy, amenities, applicationDetails]);

  const {
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    district,
    khasra_number,
    national_highway_number,
    pincode,
    state,
    village,
    address_1,
    address_2,
    highway_side,
    start_chainage,
    end_chainage,
    chainage,
  } = resolvedData;

  const formatChainage = (
    km?: string | number,
    m?: string | number,
    fallback?: string,
  ) => {
    if (km !== undefined && km !== null && m !== undefined && m !== null) {
      const kmText = String(km).trim();
      const mText = String(m).trim();
      if (kmText !== "" && mText !== "") {
        return `${kmText}+${mText}`;
      }
    }
    return fallback || "-+-";
  };

  const startChainageText = formatChainage(
    start_chainage_km,
    start_chainage_m,
    start_chainage,
  );
  const endChainageText = formatChainage(
    end_chainage_km,
    end_chainage_m,
    end_chainage,
  );
  const hasStartChainage = startChainageText !== "-+-";
  const hasEndChainage = endChainageText !== "-+-";
  const chainageText = hasStartChainage
    ? hasEndChainage
      ? ` from ${startChainageText} to ${endChainageText}`
      : startChainageText
    : chainage || "-+-";

  const ContentUndertakingsforFee = () => (
    <Box>
      <h4 className="mb-3 body-m">
        <strong>FOR NECESSARY FEE FOR THE USE OF NATIONAL HIGHWAY LAND.</strong>
      </h4>

      <p className="mb-3 body-m">
        I/ We, <strong>{profile?.name}</strong> owner/ authorised representative
        of the owner of Khasra No -<strong>{khasra_number}</strong> on National
        Highway – <strong>{national_highway_number}</strong> in existing K.M.
        Chainage -{" "}
        <strong>
          {chainageText} ({highway_side})
        </strong>{" "}
        at{" "}
        <strong>
          {` ${address_1 || "-"}, ${address_2 ? address_2 + ", " : ""}${village || "-"},
          ${district || "-"}, ${state || "-"}, ${pincode || "-"}`}
        </strong>{" "}
        do hereby solemnly declare that,
      </p>

      <ol
        className="body-m"
        style={{
          paddingLeft: "1.25rem",
          marginTop: "0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <li>
          I/ We am/ are the legal owner/ authorised representative of the owner
          of the land and premises abutting to National Highway -{" "}
          <strong>{national_highway_number}</strong> for which the access
          permission is required.
        </li>
        <li>The land is free from all encumbrances.</li>
        <li>
          I/ We will pay the necessary fee for the use of the national highway
          land whenever the fee is asked by the Highway Authorities in future.
        </li>
        <li>
          I/ We will construct the approach road at our own cost according to
          the permission granted by Highway Authorities.
        </li>
      </ol>

      <br />

      <h4 className="mb-3 body-m">
        <strong>VERIFICATION</strong>
      </h4>

      <p className="body-m mb-3">
        The contents of the affidavit/ undertaking are true and correct to the
        best of my/ our knowledge and nothing material has been concealed by me/
        us there from.
        <br />
        Verified by me/ us on <strong>{todayFormatted}</strong>
      </p>
    </Box>
  );

  const ContentUndertakingsForShifting = () => (
    <Box>
      <h3 className="mb-3 body-m">
        <strong>
          FOR NECESSARY ALTERATION INCLUDING COMPLETE REMOVAL/SHIFTING OF THE
          APPROACH ROAD
        </strong>
      </h3>

      <p className="mb-3 body-m">
        I/ We, <strong>{profile?.name || "-"}</strong> owner/ authorised
        representative of the owner of
        <strong> Khasra No - {khasra_number || "-"}</strong> on National Highway
        - <strong>{national_highway_number || "-"}</strong> in existing K.M.
        Chainage -
        <strong>
          {chainageText} ({highway_side || "-"})
        </strong>{" "}
        at
        <strong>
          {` ${address_1 || "-"}, ${address_2 ? address_2 + ", " : ""}${village || "-"},
          ${district || "-"}, ${state || "-"}, ${pincode || "-"}`}
        </strong>{" "}
        do hereby solemnly declare that,
      </p>

      <ol
        className="body-m"
        style={{
          paddingLeft: "1.25rem",
          marginTop: "0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <li>
          I/ We have applied for the access permission from National Highway{" "}
          <strong>{national_highway_number || "-"}</strong> to our{" "}
          {amenities?.property_type || "-"} as mentioned above.
        </li>
        <li>
          I/ We will carry out all necessary alteration including complete
          removal/ shifting of the approach roads at our own cost, if so
          required by the Ministry, for the development of National Highway or
          in the interest of safety in this section.
        </li>
      </ol>

      <br />

      <h4 className="mb-3 body-m">
        <strong>VERIFICATION</strong>
      </h4>

      <p className="body-m mb-3">
        The contents of the affidavit/undertaking are true and correct to the
        best of my/ our knowledge and nothing material has been concealed by me/
        us there from.
        <br />
        Verified by me/ us on <strong>{todayFormatted || "-"}</strong>
      </p>
    </Box>
  );

  const ContentUndertakingsAlterationRemoval = () => (
    <Box>
      <h4 className="mb-3 body-m">
        <strong>
          FOR FOLLOWING THE GUIDELINES AND NORMS ISSUED BY MINISTRY OF ROAD
          TRANSPORT & HIGHWAYS
        </strong>
      </h4>

      <p className="mb-3 body-m">
        I/ We, <strong>{profile?.name || "-"}</strong> owner/ authorised
        representative of the owner of
        <strong> Khasra No - {khasra_number || "-"}</strong> on National Highway
        - <strong>{national_highway_number || "-"}</strong> in existing K.M.
        Chainage -
        <strong>
          {chainageText} ({highway_side || "-"})
        </strong>{" "}
        at{" "}
        <strong>
          {` ${address_1 || "-"}, ${address_2 ? address_2 + ", " : ""}${village || "-"},
          ${district || "-"}, ${state || "-"}, ${pincode || "-"}`}
        </strong>{" "}
        do hereby solemnly declare that,
      </p>

      <p className="mb-3 body-m">
        I/ We will follow all the guidelines and norms issued by Ministry of
        Road Transport & Highways vide circular no{" "}
        <strong>RW-NH-33032/01/2017-S&R(R)</strong> dated{" "}
        <strong>26.06.2020</strong> along with all the related circulars issued
        till date including Appendix – I of circular no{" "}
        <strong>RW-NH-33032/01/2017-S&R(R)</strong> dated{" "}
        <strong>26.06.2020</strong>.
      </p>

      <br />

      <h4 className="mb-3 body-m">
        <strong>VERIFICATION</strong>
      </h4>

      <p className="mb-3 body-m">
        The contents of the affidavit/ undertaking are true and correct to the
        best of my/ our knowledge and nothing material has been concealed by me/
        us there from.
        <br />
        Verified by me/ us on <strong>{todayFormatted || "-"}</strong>
      </p>
    </Box>
  );

  const ContentUndertakingsForConstruction = () => (
    <Box>
      <h4 className="mb-3 body-m">
        <strong>UNDERTAKING FOR DRAINAGE COMPLIANCE</strong>
      </h4>

      <p className="mb-3 body-m">
        I/ We, <strong>{profile?.name || "-"}</strong> owner/ authorised
        representative of the owner of
        <strong> Khasra No - {khasra_number || "-"}</strong> on National Highway
        - <strong>{national_highway_number || "-"}</strong> in existing K.M.
        Chainage -
        <strong>
          {chainageText} ({highway_side || "-"})
        </strong>{" "}
        at
        <strong>
          {` ${address_1 || "-"}, ${address_2 ? address_2 + ", " : ""}${village || "-"},
          ${district || "-"}, ${state || "-"}, ${pincode || "-"}`}
        </strong>
        , do hereby solemnly declare that:
      </p>

      <ol
        className="body-m"
        style={{
          paddingLeft: "1.25rem",
          marginTop: "0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <li>
          The drainage design proposed for the access facility connecting to the
          National Highway strictly adheres to all standards, specifications,
          and circulars issued by the Ministry of Road Transport &amp; Highways
          (MoRTH).
        </li>
        <li>
          Design, construct, and maintain the drainage arrangement for the
          access road and its interface with the highway in such a manner that
          surface run-off is properly channeled and does not enter or obstruct
          the highway drainage network.
        </li>
        <li>
          No flooding, erosion, or weakening of the highway embankment or
          shoulder shall occur.
        </li>
        <li>
          If any damage, waterlogging, or safety hazard arises due to inadequate
          drainage, I/ We accept full responsibility to rectify the issue at our
          own cost and to bear any penalties or actions imposed by the competent
          authority.
        </li>
      </ol>

      <br />

      <h4 className="mb-3 body-m">
        <strong>VERIFICATION</strong>
      </h4>

      <p className="body-m mb-3">
        The contents of the affidavit/ undertaking are true and correct to the
        best of my/ our knowledge and nothing material has been concealed by me/
        us there from.
        <br />
        Verified by me/ us on <strong>{todayFormatted || "-"}</strong>
      </p>
    </Box>
  );

  const ContentUndertakingsCorporationAndOperational = () => (
    <Box>
      <h4 className="mb-3 body-m">
        <strong>UNDERTAKING FOR CONSTRUCTION AND OPERATIONAL STATUS</strong>
      </h4>

      <p className="mb-3 body-m">
        I/ We, <strong>{profile?.name || "-"}</strong> owner/ authorised
        representative of the owner of
        <strong> Khasra No - {khasra_number || "-"}</strong> on National Highway
        - <strong>{national_highway_number || "-"}</strong> in existing K.M.
        Chainage -
        <strong>
          {chainageText} ({highway_side || "-"})
        </strong>{" "}
        at
        <strong>
          {` ${address_1 || "-"}, ${address_2 ? address_2 + ", " : ""}${village || "-"},
          ${district || "-"}, ${state || "-"}, ${pincode || "-"}`}
        </strong>
        , do hereby solemnly declare that:
      </p>

      <ol
        className="body-m"
        style={{
          paddingLeft: "1.25rem",
          marginTop: "0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <li>
          The proposed fuel station is neither in operation nor energized as of
          the date of this undertaking.
        </li>
        <li>
          The construction of the said fuel station has not been commenced.
        </li>
        <li>
          The construction or operational activities shall begin only after
          obtaining all requisite approvals and permissions from the concerned
          Highway Authorities and regulatory bodies.
        </li>
        <li>
          Any violation or deviation from the approved norms shall make this
          undertaking liable for withdrawal of permission and other actions as
          deemed fit by the competent authority.
        </li>
      </ol>

      <br />

      <h4 className="mb-3 body-m">
        <strong>VERIFICATION</strong>
      </h4>

      <p className="body-m mb-3">
        The contents of the affidavit/ undertaking are true and correct to the
        best of my/ our knowledge and nothing material has been concealed by me/
        us there from.
        <br />
        Verified by me/ us on <strong>{todayFormatted || "-"}</strong>
      </p>
    </Box>
  );

  const ContentUndertakingsLegacyApplication = () => (
    <Box>
      <h4 className="mb-3 body-m">
        <strong>
          UNDERTAKING FOR SUBMISSION OF LEGACY / EXISTING APPLICATION ON RAJMARG
          PRAVESH PORTAL
        </strong>
      </h4>

      <p className="mb-3 body-m">
        I/We, <strong>{profile?.name || "-"}</strong> owner/authorized
        representative of the owner, hereby declare that the details, documents,
        drawings, and information submitted on the Rajmarg Pravesh Portal
        against this existing/legacy application are true and correct to the
        best of my/our knowledge.
      </p>

      <p className="mb-4 body-m">
        I/We understand that this application is being processed through the
        Rajmarg Pravesh Portal for further scrutiny, approval, Provisional NOC,
        Final NOC, renewal, or related actions as per applicable
        MoRTH/NHAI/NHIDCL guidelines.
      </p>

      <p className="body-m">
        I/We further undertake that if any incorrect information, discrepancy,
        false declaration, or mismatch with the original records is found at any
        stage in future, the concerned authority may reject or cancel the
        NOC/approval/permission issued for this application as per applicable
        Government guidelines.
      </p>

      <br />

      <h4 className="mb-3 body-m">
        <strong>VERIFICATION</strong>
      </h4>

      <p className="body-m mb-3">
        The contents of this undertaking are true and correct to the best of
        my/our knowledge and nothing material has been concealed therefrom.
        <br />
        Verified by me/ us on <strong>{todayFormatted || "-"}</strong>
      </p>
    </Box>
  );

  return (
    <div>
      <Box>
        <div id="undertaking-modal-description">
          {(document_id === 53 || document_id === 48) && (
            <ContentUndertakingsforFee />
          )}
          {(document_id === 49 || document_id === 29) && (
            <ContentUndertakingsForShifting />
          )}
          {(document_id === 30 || document_id === 50) && (
            <ContentUndertakingsAlterationRemoval />
          )}
          {(document_id === 126 || document_id === 125) && (
            <ContentUndertakingsForConstruction />
          )}
          {document_id === 141 && (
            <ContentUndertakingsCorporationAndOperational />
          )}
          {(document_id === 210 || document_id === 209) && (
            <ContentUndertakingsLegacyApplication />
          )}
        </div>
      </Box>
    </div>
  );
};

export default ConsentDetail;
